import React from "react";
import { AlertCircle, Check, X } from "lucide-react";

// دالة تنسيق الوقت بنفس طريقة صفحة الحضور
const formatTimeForDisplay = (timeString, language) => {
  if (!timeString || timeString === "-" || timeString === "undefined") return "--:--";
  try {
    const locale = language === "ar" ? "ar-EG" : "en-US";
    const d = new Date(timeString);
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: true });
    }
    return timeString;
  } catch {
    return "--:--";
  }
};

const CheckInReminderModal = ({
  open,
  onClose,
  hasTodayCheckIn,
  hasCheckOut,
  checkInTime,
  checkOutTime,
  theme,
  language,
  t,
  lastCheckOutTime, // آخر وقت انصراف (أحدث تحديث)
  checkOutCount = 0, // عدد مرات الانصراف
}) => {
  if (!open) return null;

  const isRTL = language === "ar";
  const isDarkMode = theme === "dark";
  
  // تنسيق الأوقات بنفس الطريقة المستخدمة في صفحة الحضور
  const displayCheckInTime = formatTimeForDisplay(checkInTime, language);
  const displayCheckOutTime = formatTimeForDisplay(lastCheckOutTime || checkOutTime, language);

  const getStatusColor = () => {
    if (hasTodayCheckIn && hasCheckOut) return "#10b981"; // green
    if (hasTodayCheckIn && !hasCheckOut) return "#f59e0b"; // amber
    return "#ef4444"; // red
  };

  const getStatusMessage = () => {
    if (hasTodayCheckIn && hasCheckOut) {
      if (checkOutCount > 1) {
        return language === "ar" 
          ? `✓ تم التحديث الأخير بنجاح (${checkOutCount} انصرافات)` 
          : `✓ Last update successful (${checkOutCount} check-outs)`;
      }
      return language === "ar" ? "✓ تم الدخول والخروج بنجاح" : "✓ Checked in & out";
    }
    if (hasTodayCheckIn && !hasCheckOut) {
      return language === "ar" ? "⚠ دخول بدون خروج" : "⚠ Checked in only";
    }
    return language === "ar" ? "❌ لم تقم بتسجيل الدخول" : "❌ Not checked in";
  };

  return (
    <div
      style={{
        backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.98)",
        borderLeft: `6px solid ${getStatusColor()}`,
        borderRadius: "12px",
        padding: "20px 24px",
        marginBottom: "24px",
        boxShadow: isDarkMode 
          ? "0 4px 12px rgba(0, 0, 0, 0.3)" 
          : "0 4px 12px rgba(0, 0, 0, 0.08)",
        border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
        {/* Icon */}
        <div
          style={{
            flexShrink: 0,
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            backgroundColor: `${getStatusColor()}20`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AlertCircle size={20} color={getStatusColor()} strokeWidth={2.5} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title */}
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "700",
              color: isDarkMode ? "#f1f5f9" : "#1e293b",
              marginBottom: "8px",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {language === "ar" ? "تنبيه الحضور" : "Attendance Status"}
          </h3>

          {/* Status */}
          <p
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: getStatusColor(),
              marginBottom: "12px",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {getStatusMessage()}
          </p>

          {/* Status Details */}
          <div
            style={{
              display: "flex",
              flexDirection: isRTL ? "row" : "row",
              gap: "16px",
              fontSize: "13px",
              fontFamily: "'Cairo', sans-serif",
              flexWrap: "wrap",

            }}
          >
            {/* Check-in */}
            <div style={{ display: "flex",  alignItems: "center", gap: "6px" }}>
              {hasTodayCheckIn ? (
                <Check size={16} color="#10b981" strokeWidth={3} />
              ) : (
                <X size={16} color={isDarkMode ? "#64748b" : "#cbd5e1"} strokeWidth={3} />
              )}
              <span style={{ color: isDarkMode ? "#cbd5e1" : "#64748b" }}>
                {language === "ar" ? "الدخول:" : "Check In:"} <span style={{ fontWeight: "600", color: hasTodayCheckIn ? "#10b981" : "inherit" }}>{displayCheckInTime}</span>
              </span>
            </div>

            {/* Last Check-out (آخر تحديث) */}
            {hasCheckOut && (
              <div style={{ display: "flex", flexDirection: isRTL ? "row-reverse" : "row", alignItems: "center", gap: "6px", padding: "8px 12px", backgroundColor: isDarkMode ? "rgba(16, 185, 129, 0.15)" : "rgba(16, 185, 129, 0.08)", borderRadius: "8px", border: `1px solid rgba(16, 185, 129, 0.3)` }}>
                <Check size={16} color="#10b981" strokeWidth={3} />
                <span style={{ color: isDarkMode ? "#cbd5e1" : "#64748b" }}>
                  {language === "ar" ? "انصراف:" : "Check Out:"} <span style={{ fontWeight: "700", color: "#10b981" }}>{displayCheckOutTime || "--:--"}</span>
                </span>
                {checkOutCount > 1 && (
                  <span style={{ fontSize: "12px", color: isDarkMode ? "#94a3b8" : "#94a3b8", marginLeft: isRTL ? "0" : "8px", marginRight: isRTL ? "8px" : "0" }}>
                    ({checkOutCount} {language === "ar" ? "مرات" : "times"})
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            flexShrink: 0,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
            color: isDarkMode ? "#94a3b8" : "#cbd5e1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = isDarkMode ? "#cbd5e1" : "#94a3b8";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = isDarkMode ? "#94a3b8" : "#cbd5e1";
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default CheckInReminderModal;
